// MekaFix Design System V2 — "Deep Obsidian / Ignition Red"
// Preview only. Coexists with ds (v1). Do not import in production pages yet.

export const dsv2 = {
  // ── Surface tonal layering (dark-only) ──
  bg: '#101416',                    // background
  surfaceLowest: '#0B0F11',         // inputs
  surfaceLow: '#181C1E',            // sidebar
  surface: '#1C2023',               // cards
  surfaceHigh: '#272A2D',           // hover, raised
  surfaceHighest: '#323538',        // chips, pills

  // ── Borders / outlines ──
  outline: '#2F3237',
  outlineSoft: '#23272A',
  outlineStrong: '#3A3E42',

  // ── Text ──
  text: '#E0E3E6',
  textMuted: '#94979A',
  // 🔴 SUBIDO DE '#6C7073' POR ILEGIBLE. Medido sobre las superficies oscuras
  // reales: 3.28:1 sobre la tarjeta (#1C2023) y 3.55:1 sobre el fondo — por
  // debajo del 4.5:1 que hace falta para leer texto pequeño, y `textDim` es
  // justo el color de las etiquetas de 9-10px (los seis indicadores de Salud
  // del home, los rótulos de sección, los metadatos). Este valor da 5.91 sobre
  // el fondo, 5.24 sobre la tarjeta y 4.86 sobre la superficie más clara: pasa
  // en TODAS. Se arregla en el token y no pantalla por pantalla porque son 233
  // usos en 76 archivos, y el siguiente rótulo que se escriba nace legible.
  textDim: '#8E9296',

  // ── Brand / accents ──
  red: '#E62117',                   // ignition red (primary CTA / alerts)
  // El rojo de marca EN TEXTO PEQUEÑO. `red` sobre la tarjeta oscura da 3.59:1
  // y no llega al mínimo legible; sobre un botón relleno da igual (ahí lo que
  // se lee es el blanco encima), por eso el de marca NO se toca para
  // superficies grandes: se toca el que se usa como TINTA. 5.88:1.
  redText: '#FF6B60',
  redSoft: 'rgba(230, 33, 23, 0.12)',
  redBorder: 'rgba(230, 33, 23, 0.35)',
  redGlow: '0 0 28px rgba(230, 33, 23, 0.45)',
  redGlowStrong: '0 0 48px rgba(230, 33, 23, 0.55), 0 0 12px rgba(230, 33, 23, 0.4)',
  redNeon: '0 0 1px #E62117, 0 0 8px rgba(230,33,23,0.85), 0 0 22px rgba(230,33,23,0.55)',

  green: '#00C853',
  greenSoft: 'rgba(0, 200, 83, 0.12)',
  greenBorder: 'rgba(0, 200, 83, 0.30)',

  amber: '#FFAB00',
  amberSoft: 'rgba(255, 171, 0, 0.12)',
  amberBorder: 'rgba(255, 171, 0, 0.35)',
  amberGlow: '0 0 22px rgba(255, 171, 0, 0.30)',

  blue: '#3B82F6',
  blueSoft: 'rgba(59, 130, 246, 0.12)',

  // ── Typography ──
  font: {
    head: "'Sora', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },

  // ── Radius (tight / structured per DESIGN.md) ──
  radius: {
    xs: 2,
    sm: 3,
    md: 4,
    lg: 6,
    xl: 8,
    pill: 999,
  },

  // ── Spacing (8px base) ──
  space: {
    sidebar: 280,
    topbar: 80,
    gutter: 24,
    pageDesktop: 40,
    pageMobile: 16,
    maxContent: 1440,
  },

  // ── Shadows (subtle, dark-friendly) ──
  shadow: {
    sm: '0 1px 2px rgba(0,0,0,.35)',
    md: '0 4px 12px rgba(0,0,0,.45)',
    lg: '0 20px 40px rgba(0,0,0,.55)',
    glass: '0 12px 32px rgba(0,0,0,.45)',
  },

  // ── Effects ──
  glass: {
    bg: 'rgba(26, 28, 30, 0.7)',
    border: 'rgba(47, 50, 55, 0.55)',
    blur: 'blur(14px)',
  },

  // Gradient for vehicle card image overlay (bottom-to-top)
  vehicleGradient:
    'linear-gradient(to top, rgba(16,20,22,1) 0%, rgba(16,20,22,.55) 45%, rgba(16,20,22,0) 100%)',

  // Background ambient blobs (FX overlay)
  fx: {
    redBlob: 'radial-gradient(circle, rgba(230,33,23,0.32) 0%, transparent 65%)',
    blueBlob: 'radial-gradient(circle, rgba(59,130,246,0.14) 0%, transparent 70%)',
  },

  // Neon scanline / grid (subtle "tech dashboard" feel)
  neonGrid:
    'repeating-linear-gradient(90deg, transparent 0, transparent 79px, rgba(230,33,23,0.04) 80px), repeating-linear-gradient(0deg, transparent 0, transparent 79px, rgba(230,33,23,0.04) 80px)',
};

// ─────────────────────────────────────────────────────────────
// LIGHT VARIANT — "Mechanical Precision"
// Spec entregado por diseñador externo (DESIGN.md). Light mode
// limpio, airy, con cards blancas sobre canvas slate y sombras
// ambient en lugar de bordes. Ignition Red se mantiene como brand.
// ─────────────────────────────────────────────────────────────
export const dsv2Light = {
  bg: '#F7F9FB',                    // canvas (level 0)
  surfaceLowest: '#FFFFFF',         // inputs
  surfaceLow: '#F2F4F6',            // sidebar / secondary surfaces
  surface: '#FFFFFF',               // cards (level 1, máximo contraste)
  surfaceHigh: '#ECEEF0',           // hover / raised
  surfaceHighest: '#E6E8EA',        // chips, pills

  outline: '#E2E8F0',               // slate-200 — soft border default
  outlineSoft: '#F1F5F9',           // slate-100 — divisores muy sutiles
  outlineStrong: '#CBD5E1',         // slate-300 — bordes activos

  text: '#0F172A',                  // slate-900 — dark slate (premium, no negro puro)
  textMuted: '#475569',             // slate-600 — inactive nav, body secundario
  // 🔴 SUBIDO DE '#64748B' (slate-500). Sobre blanco daba 4.76:1 y parecía
  // suficiente, pero sobre la barra lateral (#F2F4F6) caía a **4.32:1**, por
  // debajo del mínimo — el caso que se escapa si uno mide solo contra blanco.
  // Lo cazó la prueba de contraste, que mide cada tinta contra las TRES
  // superficies reales. Este valor da 5.43 / 5.14 / 4.92.
  textDim: '#5D6B7E',               // slate-500 oscurecido — labels, metadatos

  red: '#E62117',                   // ignition red (brand intacto)
  // Mismo criterio que en oscuro: el rojo de marca sobre el lienzo claro
  // (#F7F9FB) da 4.33:1 y se queda corto para texto pequeño. Como TINTA se usa
  // éste (6.06:1 sobre blanco, 5.75 sobre el lienzo); los rellenos siguen con
  // el de marca.
  redText: '#C4141B',
  redStrong: '#BE0003',             // darker red para hover/pressed
  redSoft: '#FEF2F2',               // bg suavísimo para sidebar active / hover
  redBorder: 'rgba(230, 33, 23, 0.35)',
  redGlow: '0 0 20px rgba(230, 33, 23, 0.22)',
  redGlowStrong: '0 0 28px rgba(230, 33, 23, 0.28), 0 0 8px rgba(230, 33, 23, 0.22)',
  redNeon: '0 0 1px #E62117, 0 0 6px rgba(230,33,23,0.45), 0 0 12px rgba(230,33,23,0.28)',

  green: '#10B981',                 // emerald-500 (status healthy)
  greenSoft: 'rgba(16, 185, 129, 0.10)',
  greenBorder: 'rgba(16, 185, 129, 0.35)',

  amber: '#D97706',                 // amber-600 (warnings)
  amberSoft: 'rgba(217, 119, 6, 0.10)',
  amberBorder: 'rgba(217, 119, 6, 0.35)',
  amberGlow: '0 0 16px rgba(217, 119, 6, 0.18)',

  blue: '#2563EB',                  // blue-600 (info)
  blueSoft: 'rgba(37, 99, 235, 0.10)',

  font: {
    head: "'Sora', system-ui, sans-serif",
    body: "'Inter', system-ui, sans-serif",
    mono: "'JetBrains Mono', ui-monospace, monospace",
  },

  radius: { xs: 2, sm: 3, md: 4, lg: 6, xl: 8, pill: 999 },

  space: {
    sidebar: 280, topbar: 80, gutter: 24,
    pageDesktop: 40, pageMobile: 16, maxContent: 1440,
  },

  // Ambient shadows (spec dice: rechazar bordes pesados, usar elevation)
  shadow: {
    sm: '0 1px 2px rgba(15, 23, 42, 0.04)',
    md: '0 4px 20px rgba(15, 23, 42, 0.05)',      // Level 1 (cards default)
    lg: '0 10px 30px rgba(15, 23, 42, 0.08)',     // Level 2 (hover/active)
    glass: '0 12px 32px rgba(15, 23, 42, 0.10)',
  },

  glass: {
    bg: 'rgba(255, 255, 255, 0.82)',
    border: 'rgba(226, 232, 240, 0.7)',
    blur: 'blur(14px)',
  },

  // Photo overlay: fade ligero hacia bg para legibilidad sin oscurecer
  vehicleGradient:
    'linear-gradient(to top, rgba(15,23,42,.55) 0%, rgba(15,23,42,.2) 45%, rgba(15,23,42,0) 100%)',

  fx: {
    redBlob: 'radial-gradient(circle, rgba(230,33,23,0.10) 0%, transparent 65%)',
    blueBlob: 'radial-gradient(circle, rgba(37,99,235,0.07) 0%, transparent 70%)',
  },

  neonGrid:
    'repeating-linear-gradient(90deg, transparent 0, transparent 79px, rgba(15,23,42,0.025) 80px), repeating-linear-gradient(0deg, transparent 0, transparent 79px, rgba(15,23,42,0.025) 80px)',
};

// Helper: status chip styles by kind
export const statusChip = (kind = 'neutral') => {
  const map = {
    healthy: { bg: dsv2.greenSoft, border: dsv2.greenBorder, color: dsv2.green },
    warning: { bg: dsv2.amberSoft, border: dsv2.amberBorder, color: dsv2.amber },
    danger:  { bg: dsv2.redSoft,   border: dsv2.redBorder,   color: dsv2.red   },
    neutral: { bg: dsv2.surfaceHighest, border: dsv2.outline, color: dsv2.textMuted },
  };
  const s = map[kind] || map.neutral;
  return {
    background: s.bg,
    border: `1px solid ${s.border}`,
    color: s.color,
    padding: '4px 10px',
    borderRadius: dsv2.radius.pill,
    fontFamily: dsv2.font.mono,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  };
};

// Same helper but resolves against the light token set.
export const statusChipLight = (kind = 'neutral') => {
  const map = {
    healthy: { bg: dsv2Light.greenSoft, border: dsv2Light.greenBorder, color: dsv2Light.green },
    warning: { bg: dsv2Light.amberSoft, border: dsv2Light.amberBorder, color: dsv2Light.amber },
    danger:  { bg: dsv2Light.redSoft,   border: dsv2Light.redBorder,   color: dsv2Light.red   },
    neutral: { bg: dsv2Light.surfaceHighest, border: dsv2Light.outline, color: dsv2Light.textMuted },
  };
  const s = map[kind] || map.neutral;
  return {
    background: s.bg,
    border: `1px solid ${s.border}`,
    color: s.color,
    padding: '4px 10px',
    borderRadius: dsv2Light.radius.pill,
    fontFamily: dsv2Light.font.mono,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
  };
};
